import secureApi from '../../utils/secureApi';
import { buildFixtureRequest } from './fixturePayloadAdapter';
import type { FixtureDraft } from './fixtureTypes';

// Fixture interface based on the actual API response
// Supports both old structure (Football: teamA/teamB/group) and new structure (Badminton/Table Tennis: side1/side2/round/eventCategory)
export interface Fixture {
  _id: string;
  matchNum: number;
  date: string;
  time?: string;
  ageCategory: {
    _id: string;
    ageCategory: string;
  } | string; // Can be object or string ID
  sports: string;
  stage?: string;
  group?: string; // Old structure (Football)
  round?: string; // New structure (Badminton/Table Tennis)
  // Old structure for Football
  teamA?: {
    _id: string;
    name: string;
    shortName: string;
    logo?: string;
  };
  teamB?: {
    _id: string;
    name: string;
    shortName: string;
    logo?: string;
  };
  participants?: Array<{
    slot: number;
    team?: string | {
      _id: string;
      name?: string;
      shortName?: string;
      logo?: string;
    } | null;
    players?: Array<{
      _id: string;
      uniqueId?: number;
      name: string;
      profilePic?: string;
    }>;
  }>;
  // New structure for Badminton/Table Tennis (team vs team or player vs player)
  side1?: {
    team?: string | null | { // Team ID (string), null, or full team object
      _id: string;
      name: string;
      shortName: string;
      logo?: string;
    };
    players?: Array<{
      _id: string;
      uniqueId?: number;
      name: string;
      profilePic?: string;
    }>;
  };
  side2?: {
    team?: string | null | { // Team ID (string), null, or full team object
      _id: string;
      name: string;
      shortName: string;
      logo?: string;
    };
    players?: Array<{
      _id: string;
      uniqueId?: number;
      name: string;
      profilePic?: string;
    }>;
  };
  eventCategory?: string; // New structure (e.g., "Men's Singles", "singles", "doubles")
  matchType?: string; // New structure (e.g., "singles", "doubles")
  // Legacy fields (kept for backward compatibility)
  team1Info?: {
    _id?: string;
    name?: string;
    shortName?: string;
    logo?: string;
    players?: Array<{
      _id: string;
      uniqueId?: number;
      name: string;
      profilePic?: string;
    }>;
  };
  team2Info?: {
    _id?: string;
    name?: string;
    shortName?: string;
    logo?: string;
    players?: Array<{
      _id: string;
      uniqueId?: number;
      name: string;
      profilePic?: string;
    }>;
  };
  status: string;
  venue: string;
  venueLong?: string;
  title?: string;
  subtitle?: string;
  duration?: number;
  maxDuration?: number;
  dateTime?: string;
  dateIn24Hrs?: string;
  streamingVideoUrl?: string;
  startTime?: string;
  endTime?: string;
  timer?: number;
  liveStreamInfo?: any;
  streamRecordingInfo?: any;
  tournament?: any;
  tournamentSeason?: any;
  createdAt?: string;
  updatedAt?: string;
  featuredImage?: string | null;
  template?: {
    backgroundImage?: string | null;
    imageTitle?: string | null;
    logo?: string | null;
  };
}

// API response interface
export interface FixturesApiResponse {
  paginatedResults: Fixture[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// List fixtures for a tournament
export async function listFixtures(params: {
  page?: number;
  pageSize?: number;
  tournamentId: string;
  search?: string;
  ageCategory?: string;
  sport: string;
  status?: string;
  tournamentSeason?: string;
}): Promise<FixturesApiResponse> {
  const { page = 1, pageSize = 10, tournamentId, search, ageCategory, sport, status, tournamentSeason } = params;
  const selectedSport = requireFixtureSport(sport, "list fixtures");
  
  const queryParams = new URLSearchParams({
    pageNum: page.toString(),
    pageSize: pageSize.toString(),
  });
  
  // Always include tournament in query params
  queryParams.append('tournament', tournamentId);
  
  // Always include sport as query parameter (REQUIRED by API)
  queryParams.append('sport', selectedSport);
  
  if (search) queryParams.append('search', search);
  if (ageCategory) queryParams.append('ageCategory', ageCategory);
  if (status) queryParams.append('status', status);
  if (tournamentSeason) queryParams.append('tournamentSeason', tournamentSeason);

  const response = await secureApi.get<any>(
    `/api/v2/tournaments/admin/fixtures/get-all-fixtures/${tournamentId}?${queryParams}`
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  // secureApi returns response.data from axios, which is the full API response
  let fixturesData: FixturesApiResponse | null = null;
  
  if (response && response.data && (response.data.paginatedResults || response.data.totalCount !== undefined)) {
    fixturesData = response.data;
  } else if (response && (response.paginatedResults || response.totalCount !== undefined)) {
    // Response is already the data structure
    fixturesData = response;
  } else {
    // Fallback: return empty structure
    return {
      paginatedResults: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: pageSize
    };
  }
  
  // Normalize each fixture in the paginated results based on sport
  if (fixturesData && fixturesData.paginatedResults && Array.isArray(fixturesData.paginatedResults)) {
    fixturesData.paginatedResults = fixturesData.paginatedResults.map((fixture: any) => 
      normalizeFixtureResponse(fixture, sport)
    );
  }
  
  // Ensure fixturesData is never null
  if (!fixturesData) {
    return {
      paginatedResults: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: pageSize
    };
  }
  
  return fixturesData;
}

function normalizeFixtureResponse(fixtureData: any, sport?: string): any {
  if (!fixtureData || !fixtureData._id) return fixtureData;

  const normalized = { ...fixtureData };
  const sportKey = (sport || fixtureData.sports || '').replace(/[\s_-]/g, '').toLowerCase();
  const isFootball = sportKey === 'football';

  if (isFootball) {
    normalized.teamA ||= normalized.team1Info || normalized.side1?.team;
    normalized.teamB ||= normalized.team2Info || normalized.side2?.team;
    normalized.group ||= normalized.round;
    return normalized;
  }

  // Read compatibility aliases only when hydrating legacy API responses. All
  // outgoing requests are built by buildFixtureRequest and use participants.
  normalized.participants ||= [
    normalized.side1 || {
      team: normalized.teamA || normalized.team1Info?._id,
      players: normalized.team1Info?.players,
    },
    normalized.side2 || {
      team: normalized.teamB || normalized.team2Info?._id,
      players: normalized.team2Info?.players,
    },
  ].filter((participant: any) => participant?.team || participant?.players?.length)
    .map((participant: any, index: number) => ({
      slot: index + 1,
      ...(participant.team ? { team: participant.team } : {}),
      ...(participant.players?.length ? { players: participant.players } : {}),
    }));

  normalized.round ||= normalized.group;
  return normalized;
}

export function buildCanonicalFixturePayload(
  sport: string,
  payload: FixtureDraft
): Record<string, unknown> {
  return buildFixtureRequest(sport, payload);
}

function requireFixtureSport(sport: string | undefined, operation = "create or edit a fixture"): string {
  const selectedSport = sport?.trim();
  if (!selectedSport) {
    throw new Error(`A sport is required to ${operation}.`);
  }
  return selectedSport;
}

function transformFixturePayload(payload: FixtureDraft, sport: string): Record<string, unknown> {
  return buildCanonicalFixturePayload(sport, payload);
}

// Add new fixture
// Supports both old structure (Football) and new structure (Badminton/Tennis)
export async function addFixture(payload: FixtureDraft, sport: string) {
  const selectedSport = requireFixtureSport(sport);
  const transformedPayload = transformFixturePayload(payload, selectedSport);
  
  const url = `/api/v2/tournaments/admin/fixtures/add?sport=${encodeURIComponent(selectedSport)}`;
  
  const response = await secureApi.post<any>(url, transformedPayload);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

// Edit fixture
// Supports both old structure (Football) and new structure (Badminton/Tennis)
export async function editFixture(id: string, payload: FixtureDraft, sport: string) {
  const selectedSport = requireFixtureSport(sport);
  const transformedPayload = transformFixturePayload(payload, selectedSport);
  
  const url = `/api/v2/tournaments/admin/fixtures/edit/${id}?sport=${encodeURIComponent(selectedSport)}`;
  
  const response = await secureApi.post<any>(url, transformedPayload);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

// Artwork edits intentionally bypass the full fixture payload adapter. A media save
// must not resubmit schedule, participants, scores, or the stored event category.
export async function updateFixtureArtwork(
  id: string,
  sport: string,
  field: 'featuredImage',
  imageKey: string | null,
) {
  const selectedSport = requireFixtureSport(sport, 'update match artwork');
  const payload = { [field]: imageKey };
  const response = await secureApi.post<any>(
    `/api/v2/tournaments/admin/fixtures/edit/${id}?sport=${encodeURIComponent(selectedSport)}`,
    payload,
  );
  return response?.data ?? response;
}

// Delete fixture
export async function deleteFixture(id: string, sport?: string) {
  let url = `/api/v2/tournaments/admin/fixtures/delete/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, {});
}

// Get fixture by ID
export async function getFixtureById(fixtureId: string, sport?: string) {
  let url = `/api/v2/tournaments/admin/fixtures/get-by-id/${fixtureId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  // secureApi returns response.data from axios, which is the full API response
  let fixtureData: any = null;
  
  if (response && response.data && response.data._id) {
    fixtureData = response.data;
  } else if (response && response._id) {
    // Response is already the fixture data
    fixtureData = response;
  } else {
    // Fallback: return response as-is
    return response;
  }
  
  // Normalize the fixture response based on sport
  const normalizedFixture = normalizeFixtureResponse(fixtureData, sport);
  
  // Return in the same structure as before for backward compatibility
  if (response && response.data && response.data._id) {
    return { ...response, data: normalizedFixture };
  } else {
    return normalizedFixture;
  }
}

// Bulk upload interfaces
export interface PresignedUrlRequest {
  fileName: string;
  contentType: string;
  context: string;
}

export interface PresignedUrlResponse {
  presignedUrl: string;
  fileKey?: string;
  key?: string; // API might return 'key' instead of 'fileKey'
}

export interface BulkUploadRequest {
  fileKey: string;
}

// Get presigned URL for file upload
export async function getPresignedUrl(data: PresignedUrlRequest): Promise<PresignedUrlResponse> {
  const response = await secureApi.post<any>(
    '/api/v1/general/presigned-url',
    data
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", message: "...", data: { presignedUrl: "...", key: "..." } }
  // secureApi returns response.data from axios, which is the full API response
  if (response && response.data && (response.data.presignedUrl || response.data.key)) {
    // Response has nested data object
    return {
      presignedUrl: response.data.presignedUrl,
      key: response.data.key,
      fileKey: response.data.key || response.data.fileKey // Support both 'key' and 'fileKey'
    };
  } else if (response && (response.presignedUrl || response.key)) {
    // Response is already the data structure (fallback)
    return {
      presignedUrl: response.presignedUrl,
      key: response.key,
      fileKey: response.key || response.fileKey
    };
  } else {
    // Fallback: return response as-is
    return response;
  }
}

export async function uploadImageWithPresignedUrl(file: File, context: string): Promise<string> {
  const response = await getPresignedUrl({
    fileName: file.name,
    contentType: file.type,
    context,
  });

  const key = response.key || response.fileKey;
  if (!response.presignedUrl || !key) {
    throw new Error('Failed to get presigned URL');
  }

  const uploadResponse = await fetch(response.presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image');
  }

  return key;
}

// Bulk upload fixtures
// Updated to use new endpoint format: /api/v2/tournaments/admin/fixtures/bulk-upload/{tournamentId}/{tournamentSeasonId}
// Note: sport parameter is REQUIRED by the API
export async function bulkUploadFixtures(tournamentId: string, data: BulkUploadRequest, tournamentSeasonId: string | undefined, sport: string): Promise<any> {
  let url = `/api/v2/tournaments/admin/fixtures/bulk-upload/${tournamentId}`;
  
  // If tournamentSeasonId is provided, append it to the URL path
  if (tournamentSeasonId) {
    url += `/${tournamentSeasonId}`;
  }
  
  // Add sport as query parameter (REQUIRED by API)
  // Always use '?' for the first query parameter, regardless of whether tournamentSeasonId is in path
  url += `?sport=${encodeURIComponent(sport)}`;
  
  const response = await secureApi.post<any>(url, data);
  return response;
}

// Fetch live scores for a match
export async function fetchLiveScores(matchId: string, sport: string) {
  return await secureApi.get<any>(
    `/api/v2/tournaments/manage/fetch-live-scores/${matchId}?sport=${encodeURIComponent(sport)}`
  );
}

// Update live score event
// API endpoint: /api/v2/tournaments/manage/update-live-score/{matchId}
// The eventId should be passed in the payload as _id
export async function updateLiveScore(
  matchId: string,
  payload: Record<string, unknown>,
  sport: string
) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/update-live-score/${matchId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
}

// Delete live score event
export async function deleteLiveScoreEvent(matchId: string, eventId: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/delete-event/${matchId}/${eventId}?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

// Add live score event
export async function addLiveScore(
  matchId: string,
  payload: Record<string, unknown>,
  sport: string
) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/add-live-score/${matchId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
}

// Timer management APIs
export async function startTimer(matchId: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/start?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function pauseTimer(matchId: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/pause?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function resumeTimer(matchId: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/resume?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function updateTimer(matchId: string, timer: number, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/update?sport=${encodeURIComponent(sport)}`,
    { elapsedSeconds: timer }
  );
}

export async function endTimer(matchId: string, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/end?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

// Update player of the match
export async function updatePlayerOfTheMatch(matchId: string, payload: {
  playerOfTheMatch: {
    player: string | null;
    team: string | null;
  };
  secondPlayerOfTheMatch: {
    player: string | null;
    team: string | null;
  };
}, sport: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/update-player-of-the-match/${matchId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
}

// Declare walkover for a match
export async function declareWalkover(matchId: string, teamId: string, reason: string, sport?: string) {
  let url = `/api/v2/tournaments/manage/walkover/${matchId}/${teamId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, { reason });
}

// Abandon (cancel) a match
export async function abandonMatch(matchId: string, reason: string, sport?: string) {
  let url = `/api/v2/tournaments/manage/cancelled/${matchId}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, { reason });
}

// Backwards-compatible wrappers for callers that previously used the retired
// /v2/multi-sports/manage/:sport/:match endpoint family.
export async function startMatchMultiSports(sport: string, matchId: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/start?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function pauseMatchMultiSports(sport: string, matchId: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/pause?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function resumeMatchMultiSports(sport: string, matchId: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/resume?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function endMatchMultiSports(sport: string, matchId: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/end?sport=${encodeURIComponent(sport)}`,
    {}
  );
}

export async function cancelMatchMultiSports(sport: string, matchId: string, reason: string) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/cancelled/${matchId}?sport=${encodeURIComponent(sport)}`,
    { reason }
  );
}

export async function updateTimerMultiSports(sport: string, matchId: string, timer: number) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/timer/${matchId}/update?sport=${encodeURIComponent(sport)}`,
    { elapsedSeconds: timer }
  );
}

// Sets management APIs (for Badminton and Table Tennis)
export interface SetData {
  _id?: string;
  setNumber: number;
  side1Points: number;
  side2Points: number;
  isComplete: boolean;
  winner?: 'side1' | 'side2';
}

// Get sets from fixture data (sets are included in fixture response)
// Uses getFixtureById which returns the full fixture including sets
export async function getSets(matchId: string, sport: string) {
  const fixtureResponse = await getFixtureById(matchId, sport);
  // Handle different response structures from getFixtureById
  const fixture = fixtureResponse?.data || fixtureResponse;
  return fixture?.sets || [];
}

// Create a new set (only when previous sets are complete)
// Uses same endpoint as Football but with empty payload for creating sets
export async function addSet(matchId: string, sport: string, initialPoints?: { side1Points?: number; side2Points?: number }) {
  const payload: any = {};
  
  // Include initial points if provided (default to 0)
  if (initialPoints !== undefined) {
    payload.side1Points = initialPoints.side1Points ?? 0;
    payload.side2Points = initialPoints.side2Points ?? 0;
  }
  
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/add-live-score/${matchId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
}

// Update set score, mark complete, or delete
// Uses same endpoint as Football but with set-specific payload (setNumber, side1Points, side2Points, status, action)
export async function updateSet(matchId: string, sport: string, payload: {
  setNumber: number;
  side1Points?: number;
  side2Points?: number;
  side?: 'side1' | 'side2';
  status?: 'completed';
  action?: 'delete';
}) {
  return await secureApi.post<any>(
    `/api/v2/tournaments/manage/update-live-score/${matchId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
}

// Legacy functions for backward compatibility
export async function addOrUpdateSet(matchId: string, sport: string, setData: SetData) {
  if (setData.setNumber && (setData.side1Points !== undefined || setData.side2Points !== undefined)) {
    return await updateSet(matchId, sport, {
      setNumber: setData.setNumber,
      side1Points: setData.side1Points,
      side2Points: setData.side2Points,
      ...(setData.isComplete ? { status: 'completed' } : {})
    });
  }
  throw new Error('Invalid set data');
}

export async function markSetComplete(matchId: string, sport: string, setNumber: number) {
  return await updateSet(matchId, sport, {
    setNumber,
    status: 'completed'
  });
}

export async function deleteSet(matchId: string, sport: string, setNumber: number) {
  return await updateSet(matchId, sport, {
    setNumber,
    action: 'delete'
  });
}

// Get calendar events (dates with matches) for a tournament
export interface CalendarEvent {
  date: string; // Format: YYYY-MM-DD
  count?: number; // Number of matches on this date
}

export async function getCalendarEvents(
  tournamentId: string,
  sport: string = 'Football',
  tournamentSeason?: string
): Promise<CalendarEvent[]> {
  // Build URL with sport as query parameter (singular, as required by API)
  let url = `/api/v2/tournaments/admin/fixtures/calendar-events/${tournamentId}?sport=${encodeURIComponent(sport)}`;
  
  // Add tournamentSeason as query parameter if provided
  if (tournamentSeason) {
    url += `&tournamentSeason=${tournamentSeason}`;
  }
  
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure
  // API returns: { statusCode: 200, status: "success", data: [{ date: "YYYY-MM-DD", matches: [...] }] }
  let eventsArray: any[] = [];
  
  if (response && response.data && Array.isArray(response.data)) {
    eventsArray = response.data;
  } else if (Array.isArray(response)) {
    eventsArray = response;
  }
  
  // Transform array of event objects (with date and matches) to CalendarEvent objects
  return eventsArray
    .filter((event: any) => event != null) // Filter out null/undefined
    .map((event: any) => {
      // Handle both old format (date string) and new format (object with date and matches)
      let dateStr: string;
      let matchCount: number = 1;
      
      if (typeof event === 'string') {
        // Old format: array of date strings
        dateStr = event;
      } else if (event && typeof event === 'object' && event.date) {
        // New format: object with date and matches array
        dateStr = typeof event.date === 'string' ? event.date : String(event.date);
        // Count matches if matches array exists
        matchCount = Array.isArray(event.matches) ? event.matches.length : 1;
      } else {
        // Fallback: try to convert to string
        dateStr = String(event);
      }
      
      // Normalize date format (remove time if present)
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }
      
      return {
        date: dateStr,
        count: matchCount
      };
    })
    .filter((event: CalendarEvent) => event.date != null && event.date.trim() !== ''); // Filter out invalid dates
}

// Download fixtures Excel for a tournament with date range filters
// API returns JSON with S3 URL in data field: { statusCode: 200, status: "success", message: "...", data: "https://s3-url..." }
// Returns the S3 URL for direct download
// Updated: tournamentSeasonId is now always required in the URL path: /api/v2/tournaments/admin/fixtures/excel/{tournamentId}/{tournamentSeasonId}?...
export async function downloadFixturesExcelWithFilters(
  tournamentId: string,
  params: {
    startDate: string; // Format: YYYY-MM-DD
    endDate: string; // Format: YYYY-MM-DD
    status?: 'pending' | 'completed'; // Optional status filter
    ageCategory?: string; // Optional age category ID
    sports?: string; // Optional sports type, defaults to 'Football'
    tournamentSeason: string; // Required tournamentSeason ID (always in URL path)
  }
): Promise<string> {
  // Build URL path - tournamentSeasonId is always required in the path
  if (!params.tournamentSeason) {
    throw new Error('tournamentSeason is required for downloading fixtures Excel');
  }
  const urlPath = `/api/v2/tournaments/admin/fixtures/excel/${tournamentId}/${params.tournamentSeason}`;

  // Build query parameters
  // Note: API expects 'sport' (singular) in query params, not 'sports'
  const queryParams = new URLSearchParams({
    sport: params.sports || 'Football', // Use 'sport' (singular) for query param
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.ageCategory) {
    queryParams.append('ageCategory', params.ageCategory);
  }

  // Note: tournamentSeason is NOT in query params anymore - it's in the URL path

  // Get the S3 URL from the API (JSON response)
  const response = await secureApi.get<any>(
    `${urlPath}?${queryParams.toString()}`,
    {
      responseType: 'json',
      timeout: 30000,
    }
  );

  // Extract S3 URL from response
  let s3Url: string;
  if (typeof response === 'string') {
    s3Url = response;
  } else if (response && typeof response === 'object') {
    // Handle JSON response with data field containing the S3 URL
    // Response structure: { statusCode: 200, status: "success", message: "...", data: "https://..." }
    s3Url = response.data || response.url || response.fileUrl || response.downloadUrl;
  } else {
    throw new Error('Server did not return a valid S3 URL');
  }

  if (!s3Url || typeof s3Url !== 'string') {
    throw new Error('Server did not return a valid S3 URL');
  }

  // Return the S3 URL for direct download
  return s3Url;
}
